import { Product, ProductVariant } from '../../types/product-variant';
import { useState, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { motion, AnimatePresence } from 'framer-motion';
import { useMedia } from '../../context/MediaContext';
import { orderProductMedia } from '../../utils/media';
import { cloudinaryUrl, cloudinaryVideoUrl } from '../../utils/cloudinary';

interface ProductImagesProps {
    product: Product;
    selectedVariant: ProductVariant | null;
    onImageClick: (imageSrc: string) => void;
}

interface CarouselItem {
    type: 'image' | 'video';
    src: string;
}

const ProductImages = ({ product, selectedVariant, onImageClick }: ProductImagesProps) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [direction, setDirection] = useState(0);
    const carouselRef = useRef<HTMLDivElement>(null);

    const { getMediaByProduct, getGenericPackaging } = useMedia();

    const resolveProductMedia = () => {
        let media = product.airtableId ? getMediaByProduct(product.airtableId) : [];
        if (media.length === 0 && product.id) {
            media = getMediaByProduct(product.id);
        }
        if (media.length === 0 && product.slug) {
            media = getMediaByProduct(product.slug);
        }
        return media;
    };

    const productMedia = resolveProductMedia();
    const ordered = orderProductMedia(productMedia);
    // Variant images override media when a variant is selected
    const variantImages: string[] = selectedVariant?.images
        ? (Array.isArray(selectedVariant.images)
            ? selectedVariant.images
            : [selectedVariant.images.main, selectedVariant.images.model, selectedVariant.images.zoom].filter(Boolean) as string[])
        : [];

    // Unified media list — order comes from usage tags in DB, no image/video splitting
    const allMedia: CarouselItem[] = variantImages.length > 0
        ? variantImages.map(src => ({ type: (src.includes('/video/upload/') ? 'video' : 'image') as 'image' | 'video', src }))
        : ordered.map(m => ({ type: (m.mediaType ?? 'image') as 'image' | 'video', src: m.cloudinaryURL! }));

    const packaging = getGenericPackaging();
    const heroItem = allMedia[0];
    const heroImage = heroItem?.src ?? '';

    // Carousel: everything after hero, in usage-tag order
    const carouselMedia: CarouselItem[] = allMedia.slice(1)
        .filter((item, i, arr) => arr.findIndex(x => x.src === item.src) === i);

    // If there's only 1 carousel item (product has 2 images), add packaging so there's a pair
    // If odd count, pad with packaging to keep the 2-column grid even
    const sortedMedia = [...carouselMedia];
    if (sortedMedia.length > 0 && sortedMedia.length % 2 === 1) {
        sortedMedia.push({ type: 'image' as const, src: packaging });
    }

    const hasCarousel = sortedMedia.length > 2;
    const totalPages = Math.ceil(sortedMedia.length / 2);
    const currentPage = Math.floor(currentIndex / 2);

    const goToPage = (page: number) => {
        const newIndex = page * 2;
        setDirection(newIndex > currentIndex ? 1 : -1);
        setCurrentIndex(newIndex);
    };

    const visibleMedia = sortedMedia.slice(currentIndex, currentIndex + 2);

    return (
        <div className="space-y-4">
            {/* Main Image / Video */}
            {heroImage && (
                heroItem?.type === 'video' ? (
                    <video
                        src={cloudinaryVideoUrl(heroImage)}
                        className="w-full h-auto rounded-lg shadow-lg"
                        autoPlay
                        loop
                        muted
                        playsInline
                    />
                ) : (
                    <img
                        src={cloudinaryUrl(heroImage, 800)}
                        alt={product?.name}
                        className="w-full h-auto rounded-lg shadow-lg cursor-pointer hover:opacity-75 transition-opacity"
                        onClick={() => onImageClick(heroImage)}
                        width={800}
                        height={800}
                    />
                )
            )}

            {/* Carousel Thumbnails */}
            {sortedMedia.length > 0 && (
                <div className="relative">
                    {/* Left Arrow */}
                    {hasCarousel && currentPage > 0 && (
                        <button
                            onClick={() => goToPage(currentPage - 1)}
                            className="absolute left-2 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white shadow-lg rounded-full w-8 h-8 flex items-center justify-center transition-all"
                            aria-label="Previous images"
                        >
                            <FontAwesomeIcon icon={faChevronLeft} className="text-gray-700 text-sm" />
                        </button>
                    )}

                    {/* Thumbnails Grid - Carousel Container */}
                    <div className="relative overflow-hidden h-48">
                        <AnimatePresence initial={false} custom={direction}>
                            <motion.div
                                key={currentPage}
                                custom={direction}
                                initial={{ x: direction > 0 ? '100%' : '-100%' }}
                                animate={{ x: 0 }}
                                exit={{ x: direction > 0 ? '-100%' : '100%' }}
                                transition={{ type: "tween", duration: 0.3, ease: "easeInOut" }}
                                className="grid grid-cols-2 gap-4 absolute inset-0"
                            >
                                {visibleMedia.map((media, index) => (
                                    <div
                                        key={currentIndex + index}
                                        className="relative cursor-pointer group/item"
                                        onClick={() => onImageClick(media.src)}
                                    >
                                        {media.type === 'image' ? (
                                            <img
                                                src={cloudinaryUrl(media.src, 400)}
                                                alt={`${product?.name} view ${currentIndex + index + 1}`}
                                                className="w-full h-48 object-cover rounded-lg shadow-md hover:opacity-75 transition-opacity"
                                                loading="lazy"
                                                width={400}
                                                height={192}
                                            />
                                        ) : (
                                            <video
                                                src={cloudinaryVideoUrl(media.src)}
                                                className="w-full h-48 object-cover rounded-lg shadow-md"
                                                autoPlay
                                                loop
                                                muted
                                                playsInline
                                            />
                                        )}
                                    </div>
                                ))}
                            </motion.div>
                        </AnimatePresence>
                    </div>

                    {/* Right Arrow */}
                    {hasCarousel && currentPage < totalPages - 1 && (
                        <button
                            onClick={() => goToPage(currentPage + 1)}
                            className="absolute right-2 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white shadow-lg rounded-full w-8 h-8 flex items-center justify-center transition-all"
                            aria-label="Next images"
                        >
                            <FontAwesomeIcon icon={faChevronRight} className="text-gray-700 text-sm" />
                        </button>
                    )}

                    {/* Pagination Dots */}
                    {hasCarousel && (
                        <div className="flex justify-center gap-1.5 mt-3">
                            {Array.from({ length: totalPages }).map((_, pageIndex) => (
                                <div
                                    key={pageIndex}
                                    className={`w-2 h-2 rounded-full transition-colors ${
                                        currentPage === pageIndex
                                            ? 'bg-brand-blue'
                                            : 'bg-gray-300'
                                    }`}
                                />
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default ProductImages;
